import { FC, useEffect, KeyboardEvent, useMemo, useCallback } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import StarIcon from '@icons/star_required_icon.svg?component';
import { useValidation } from '@hooks/useValidation';
import { IInputProps } from './types';

const basicInputStyle =
  'w-full pl-4 pr-[16px] py-1 border-[1.5px] border-custom-gray rounded-[8px] bg-white leading-7 placeholder:text-[16px] placeholder:text-[rgba(23,30,55,0.3)] hover:border-[#414042] focus:outline-none focus:ring-0 focus:border-[#414042]';

export const Input: FC<IInputProps> = (props) => {
  const {
    type,
    styleProp,
    icon,
    name,
    label,
    required = false,
    myOnChange,
    myOnFocus,
    myOnBlur,
    registerPattern,
    validation,
    showRequiredIcon = false,
    requiredIconPlace = 'label',
    min,
    max,
    iconPosition = 'left',
    disabled,
    iconStyleProp,
    placeholder,
    minLength,
    ...inputProps
  } = props;
  const formContext = useFormContext();
  const { getMinLengthRule, getErrorMessageByType } = useValidation();

  const { control, setValue, watch } = formContext;

  useEffect(() => {
    if (type === 'phone') {
      const phone: string | undefined = watch(name);
      phone !== undefined &&
        phone.length > 0 &&
        !phone.startsWith('+') &&
        setValue(name, `+${phone}`);
    }
    if (type === 'text') {
      const text: string | undefined = watch(name);
      text && setValue(name, text.trim());
    }
  }, []);

  const valueAs = useCallback(
    (val: string) => {
      if (type === 'money' && val) {
        if (typeof val === 'number') return Number((val as number).toFixed(2));
        const vals = val.toString().split('.');
        if (vals[1]) return +`${vals[0]}.${vals[1].slice(0, 2)}`;
        else return Number((+vals[0]).toFixed(2));
      }
      if (type === 'number' && val) {
        return +val;
      }
      if (type === 'text' && val) {
        return val.trim();
      }
      return val;
    },
    [type]
  );

  const onBlurHandler = useCallback(
    (val: string) => {
      const valAs = valueAs(val);
      setValue(name, valAs);
      myOnBlur && myOnBlur(valAs);
    },
    [name, type]
  );

  const onKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      (e.target as HTMLElement).blur();
    }
  }, []);

  const inputStyle = useMemo(() => {
    let style = basicInputStyle;
    if (icon) {
      if (iconPosition === 'left') style = `${style} pl-[52px]`;
      else {
        if (required && showRequiredIcon && requiredIconPlace === 'input')
          style = `${style} pr-[62px]`;
        else style = `${style} pr-[40px]`;
      }
    }
    if (styleProp) {
      style = `${style} ${styleProp}`;
    }
    if (disabled) {
      style = `${style} !bg-[#4140421A]`;
    }
    return style;
  }, [name, icon, iconPosition, styleProp, disabled, required, showRequiredIcon]);

  const iconStyle = useMemo(() => {
    let style =
      'absolute top-1/2 translate-y-[-50%] w-[24px] h-[24px] opacity-30  group-focus-within:opacity-100 flex-centred';
    if (iconStyleProp) style = `${style} ${iconStyleProp}`;
    if (iconPosition === 'left') return `${style} left-4`;
    else {
      if (required && showRequiredIcon && requiredIconPlace === 'input')
        return `${style} right-[38px]`;
      return `${style} right-[16px]`;
    }
  }, [iconPosition, required, showRequiredIcon]);

  return (
    <div className="relative flex flex-col items-start justify-center w-full">
      {label && (
        <div className="label">
          <p>{label}</p>
          {required && showRequiredIcon && requiredIconPlace === 'label' ? (
            <StarIcon width={12} height={12} />
          ) : null}
        </div>
      )}
      <label className="relative block w-full group">
        {/* use ts-ignore due to Type instantiation is excessively deep and possibly infinite.ts(2589) error */}
        {/* @ts-ignore */}
        <Controller
          control={control}
          name={name}
          rules={{
            required: required,
            validate: validation?.validate,
            pattern: registerPattern,
            min,
            max,
            ...(minLength !== undefined && { minLength: getMinLengthRule(minLength) }),
          }}
          render={({ field, field: { onChange, value }, fieldState: { error } }) => (
            <>
              <input
                {...field}
                placeholder={placeholder}
                aria-label={label || placeholder || name}
                className={`${inputStyle} ${error && '!border-error'}`}
                type={type === 'money' ? 'number' : type}
                // defaultValue={value}
                value={value ?? ''}
                onFocus={(e) => {
                  myOnFocus && myOnFocus(e.target.value);
                }}
                onBlur={(e) => {
                  onBlurHandler(e.target.value);
                }}
                onKeyDown={onKeyDown}
                onChange={(e) => {
                  if (type === 'phone') {
                    value !== undefined &&
                      value.length > 0 &&
                      !value.startsWith('+') &&
                      onChange(`+${value}`);
                  }
                  if (myOnChange) myOnChange(e.currentTarget.value);
                  else onChange(e.currentTarget.value);
                }}
                disabled={disabled}
                onWheel={(e) => e.currentTarget.blur()}
                {...inputProps}
              />
              {error && (
                <p className="text-error text-[12px] mt-[4px]">
                  {getErrorMessageByType(error.type, error.message)}
                </p>
              )}
            </>
          )}
        />
        {icon && <div className={iconStyle}>{icon}</div>}
        {required && showRequiredIcon && requiredIconPlace === 'input' ? (
          <div className={`absolute top-1/2 translate-y-[-50%] right-[16px] `}>
            <StarIcon width={12} height={12} />
          </div>
        ) : null}
      </label>
    </div>
  );
};
